/**
 * Navigation utilities for canvas templates
 * Extracted from AddCanvasTemplate.js to reduce file size
 */

import { safeSetCanvasSnapshot } from "./canvasSnapshotStorage";
import { loadFromIndexedDB } from "../../../util/indexedDBUtils";

/**
 * Handle navigation to canvas editor
 * @param {Object} params - Navigation parameters
 * @param {number} params.canvasPageIndex - Index of canvas page
 * @param {Array} params.canvasPages - Array of canvas pages
 * @param {Object} params.template - Template object
 * @param {string|null} params.templateId - Template ID
 * @param {Array} params.partNumbers - Part numbers array
 * @param {string} params.shortDescription - Short description
 * @param {string} params.canvasName - Canvas name
 * @param {string} params.categoryName - Category name
 * @param {string} params.categoryId - Category ID
 * @param {boolean} params.initialLoading - Initial loading state
 * @param {boolean} params.snapshotGenerating - Snapshot generating state
 * @param {boolean} params.isLoading - Loading state
 * @param {Object} params.callbacks - Callback functions
 * @param {Function} params.callbacks.setIsLoading - Set loading state
 * @param {Function} params.callbacks.setSaveProgress - Set save progress
 * @param {Function} params.callbacks.navigate - Navigation function
 * @param {Function} params.callbacks.saveCanvasElementsToIndexedDB - Save to IndexedDB function
 * @param {Object} params.fromMyCanvasProcessedRef - Ref object for tracking
 * @returns {Promise<void>}
 */
export const handleNavigateToCanvas = async ({
  canvasPageIndex,
  canvasPages,
  template,
  templateId,
  partNumbers,
  shortDescription,
  canvasName,
  categoryName,
  categoryId,
  initialLoading,
  snapshotGenerating,
  isLoading,
  callbacks = {},
  fromMyCanvasProcessedRef,
}) => {
  const {
    setIsLoading,
    setSaveProgress,
    navigate,
    saveCanvasElementsToIndexedDB,
  } = callbacks;

  // Prevent navigation if still loading or generating snapshot
  if (initialLoading || snapshotGenerating || isLoading) {
    return;
  }

  const selectedPage = canvasPages[canvasPageIndex];

  // Get the correct pageId from template or generate one
  const pageId =
    template?.pages?.[0]?.canvas_page_ref ||
    selectedPage.id ||
    `template_${Date.now()}`;


  // Set loading state to prevent multiple clicks
  if (setIsLoading) setIsLoading(true);
  if (setSaveProgress) {
    setSaveProgress({ step: "Preparing canvas data...", percentage: 10 });
  }

  try {
    // Wait for IndexedDB save to complete before navigation.
    // If a row already exists, do not overwrite it with potentially stale
    // CanvasForm page state (can resurrect deleted elements).
    let existingElementsRow = null;
    try {
      existingElementsRow = await loadFromIndexedDB("canvasElements", pageId);
    } catch (_e) {
      existingElementsRow = null;
    }
    const hasExistingRow = !!existingElementsRow?.data;
    if (saveCanvasElementsToIndexedDB && !hasExistingRow) {
      await saveCanvasElementsToIndexedDB(selectedPage.elements, pageId);
    }
  } catch (error) {
    // Silently fail
  }

  // Store the canvas snapshot for instant preview in MyCanvas (may skip if over quota)
  if (selectedPage.snapshot) {
    safeSetCanvasSnapshot(selectedPage.snapshot);
  }

  // Set flags and lightweight metadata synchronously
  localStorage.setItem("fromMyCanvas", "true");
  localStorage.setItem("pageId", pageId);

  const elementCounts = {
    texts: selectedPage.elements?.texts?.length || 0,
    rectangles: selectedPage.elements?.rectangles?.length || 0,
    circles: selectedPage.elements?.circles?.length || 0,
    arrows: selectedPage.elements?.arrows?.length || 0,
    lines: selectedPage.elements?.lines?.length || 0,
    doubleSidedArrows: selectedPage.elements?.doubleSidedArrows?.length || 0,
    images: selectedPage.elements?.images?.length || 0,
    scribbles: selectedPage.elements?.scribbles?.length || 0,
  };
  localStorage.setItem("elementCounts", JSON.stringify(elementCounts));

  const stateInfo = sessionStorage.getItem("canvasStateInfo");
  const hasTextContent = stateInfo && JSON.parse(stateInfo).includesText;
  sessionStorage.setItem(
    "canvasStateInfo",
    JSON.stringify({
      hasChanges: true,
      lastUpdate: new Date().toISOString(),
      includesText: hasTextContent,
      preserveTextOnReturn: true,
    })
  );
  localStorage.setItem("short_description", shortDescription);
  localStorage.setItem("canvas_name", canvasName);
  localStorage.setItem("canvas_category_name", categoryName);
  localStorage.setItem("canvas_category_id", categoryId);

  // Reset the ref so we can process the return from canvas properly
  if (fromMyCanvasProcessedRef) {
    fromMyCanvasProcessedRef.current = false;
  }

  // Store template data for when we come back from canvas
  if (template && Object.keys(template).length > 0) {
    sessionStorage.setItem(
      "canvas_template_data",
      JSON.stringify({
        canvas_page_template_id: template.canvas_page_template_id,
        pageRef: template.pages?.[0]?.canvas_page_ref,
        textboxId: template.pages?.[0]?.text_boxes?.[0]?.text_box_id,
        templateId: templateId,
      })
    );
  } else if (templateId) {
    // If template object is not available but we have templateId, store that
    sessionStorage.setItem(
      "canvas_template_data",
      JSON.stringify({
        canvas_page_template_id: templateId,
        pageRef: null,
        textboxId: null,
        templateId: templateId,
      })
    );
  }

  // Determine canvas height from multiple sources (priority order)
  // 1. Check page elements (most specific)
  // 2. Check page direct property
  // 3. Check template's page layout_config
  // 4. Check template's layout_config
  // 5. Default to 100
  const canvasHeight =
    selectedPage.elements?.canvasHeight ??
    selectedPage.canvasHeight ??
    template?.pages?.[0]?.layout_config?.layout?.page_height_vh ??
    template?.layout_config?.layout?.page_height_vh ??
    100;

  // Navigate immediately to MyCanvas
  if (navigate) {
    navigate(`/canvas`, {
      state: {
        fromCanvasPage: true,
        hasTextContent,
        pageIndex: canvasPageIndex,
        pageId: pageId,
        partNumbers,
        fromCanvasTemplate: true,
        currentSnapshot: selectedPage.snapshot,
        layout_config: {
          layout: {
            page_height_vh: canvasHeight,
          },
        },
        page_height_vh: canvasHeight,
      },
    });
  }

  // Clear loading state after navigation
  if (setIsLoading) setIsLoading(false);
};
